import { dummyItemTotalsView } from 'checkout/lib/utils/accommodation/view'
import { DAY_MONTH_NAME_SHORT, DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { sum, unique, sortBy, EmptyArray } from 'lib/array/arrayUtils'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import moment from 'moment'
import { createSelector } from 'reselect'
import offerPageURL from 'lib/offer/offerPageURL'
import { isBundleAndSaveItem } from 'lib/checkout/checkoutUtils'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'

export const getBundleAndSaveItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.BundleAndSaveItem> => {
    return items.filter(isBundleAndSaveItem)
  },
  reselectShallowEqual,
)

export const getBundleAndSaveItemViews = createSelector(
  (state: App.State) => getBundleAndSaveItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (
    items,
    offerState,
    calendarsByPackageKey,
    availableRatesByOccupancy,
  ): App.WithDataStatus<Array<App.Checkout.BundleAndSaveView>> => {
    const offerIds = unique(items.map(item => item.offerId))
    const offers = offerIds.map(id => offerState[id]).filter(Boolean)

    // make sure we have our offer ids loaded
    if (offers.length === offerIds.length) {
      const views = items.map((item): App.Checkout.BundleAndSaveView => {
        const bundleOffer = offerState[item.offerId] as App.BundleOffer
        const bundlePackage = bundleOffer.bundlePackages[item.packageId]

        const sortedPkgOptions = sortBy(bundlePackage.options, (option) => {
          return moment(item.dates[option.fkOfferId].checkIn).toDate().getTime()
        }, 'asc')

        return {
          item,
          ...(bundleOffer ? { offerPageUrl: offerPageURL(bundleOffer) } : {}),
          bundleOffer,
          bundlePackage,
          occupancy: item.occupancy,
          hotels: sortedPkgOptions.map((option): App.Checkout.BundleAndSaveHotelView => {
            const dates = item.dates[option.fkOfferId]
            const duration = dates.duration ?? moment(dates.checkOut).diff(dates.checkIn, 'days')
            const pkg = bundleOffer.packages.find(p =>
              p.lePackageId === option.lePackageId &&
              p.roomRate?.id === option.fkRoomRateId &&
              p.duration === duration,
            )!

            const offer = bundleOffer.bundledOffers[option.fkOfferId]
            const itemTotals = getLEPackageItemTotals(
              offer,
              pkg,
              dates.checkIn,
              dates.checkOut,
              item.occupancy,
              calendarsByPackageKey,
              undefined,
              availableRatesByOccupancy,
            ) ?? dummyItemTotalsView()

            return {
              title: `${pkg.roomType.name}: ${offer.property.name}`,
              item,
              loyalty: {},
              totals: itemTotals,
              checkIn: dates.checkIn,
              checkOut: dates.checkOut,
              duration,
              offer,
              package: pkg,
            }
          }),
        }
      })

      return {
        hasRequiredData: true,
        data: views,
      }
    }

    return {
      hasRequiredData: false,
      data: [],
    }
  },
)

function getCancellationPolicyBreakdown(
  view: App.Checkout.BundleAndSaveView,
  hotelView: App.Checkout.BundleAndSaveHotelView,
): App.Checkout.CancellationPolicyBreakdownView | undefined {
  switch (hotelView.offer.type) {
    case 'hotel':
      return {
        cancellationPolicyDetails: hotelView.package.roomRate?.cancellationPolicy,
        checkInDate: moment(hotelView.checkIn),
        checkOutDate: moment(hotelView.checkIn).clone().add(hotelView.duration, 'days'),
        occupants: view.occupancy,
      }
    case 'tactical_ao_hotel':
      return {
        cancellationPolicyDetails: hotelView.package.roomRate?.cancellationPolicy,
        checkInDate: moment(hotelView.checkIn),
        checkOutDate: moment(hotelView.checkIn).clone().add(hotelView.duration, 'days'),
        occupants: view.occupancy,
        offerId: hotelView.offer.id,
        ignoreDynamicCancellationPolicy: hotelView.package.ignoreDynamicCancellationPolicy ?? false,
        useDynamicCancellationPolicies: hotelView.offer.property?.useDynamicCancellationPolicies ?? false,
        timezoneOffset: hotelView.offer.property?.timezoneOffset,
        uniqueKey: hotelView.package.uniqueKey,
      }
    default:
      return undefined
  }
}

export const getBundleAndSaveBreakdownView = createSelector(
  (state: App.State) => getBundleAndSaveItemViews(state),
  (views): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (views.data.length === 0) { return { hasRequiredData: views.hasRequiredData, data: [] } }

    const intl = getCurrentIntl()
    const allHotelViews = views.data.flatMap(data => data.hotels)
    const totalNights = sum(allHotelViews.map(view => view.duration))
    const numberOfNights = intl.formatMessage({
      id: 'ZFq6gnT3wl',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Night count in bundle and save accommodation breakdown',
    }, { count: totalNights })
    const numberOfHotels = intl.formatMessage({
      id: 'FKOaFQn36S',
      defaultMessage: '{count, plural, one {# hotel} other {# hotels}}',
      description: 'Hotel count in bundle and save accommodation breakdown',
    }, { count: allHotelViews.length })
    const guests = formatOccupantsAsGuests(...views.data.map(view => view.occupancy))

    const bundleAndSaveBreakdown: App.Checkout.PriceBreakdownView = {
      title: intl.formatMessage({
        id: 'ZtZAI4+1Pq',
        defaultMessage: 'Accommodation',
        description: 'Title for accommodation section in bundle and save price breakdown',
      }),
      price: sum(allHotelViews.map(view => view.totals.price + view.totals.surcharge - view.totals.taxesAndFees)),
      memberPrice: sum(allHotelViews.map(view => view.totals.memberPrice + view.totals.surcharge - view.totals.taxesAndFees)),
      additionalInfoText: [
        `${numberOfNights} (${numberOfHotels}, ${guests})`,
      ],
      items: views.data.flatMap<App.Checkout.BundleAndSaveItemBreakdownView>(view => {
        return view.hotels.map(hotel => {
          return {
            title: hotel.offer.property?.name || '',
            offerPageUrl: view.offerPageUrl,
            itemId: view.item.itemId,
            offerId: hotel.offer.id,
            itemType: view.item.itemType,
            price: hotel.totals.price + hotel.totals.surcharge - hotel.totals.taxesAndFees,
            memberPrice: hotel.totals.memberPrice > 0 ? hotel.totals.memberPrice + hotel.totals.surcharge - hotel.totals.taxesAndFees : 0,
            surcharge: hotel.totals.surcharge ?? 0,
            additionalInfo: [
              {
                text: `${moment(hotel.checkIn).format(DAY_MONTH_NAME_SHORT)} - ${moment(hotel.checkOut).format(DMY_CASUAL_FORMAT)} (${intl.formatMessage({
                id: '37AToVtBXX',
                defaultMessage: '{count, plural, one {# night} other {# nights}}',
                description: 'Night count for a hotel in bundle and save item',
              }, { count: hotel.duration })})`,
              },
              { text: hotel.package.name },
            ],
            cancellationPolicy: getCancellationPolicyBreakdown(view, hotel),
            additionalElements: EmptyArray,
          }
        })
      }),
    }

    return {
      hasRequiredData: true,
      data: [bundleAndSaveBreakdown],
    }
  },
)
